import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ReservationView } from '@/api/types';
import { Card, ErrorNote, Money, Reference, Spinner, Timestamp } from '@/components/Common';
import { useAuth } from '@/lib/auth';
import { duration, money } from '@/lib/format';

export function ReservationsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['reservations'],
    queryFn: () => api.get<{ reservations: ReservationView[] }>('/api/admin/reservations'),
    // The countdown is the point of this page, so it needs to actually count down.
    refetchInterval: 10_000,
  });

  const expireNow = useMutation({
    mutationFn: () => api.post('/api/admin/reservations/expire-now'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reservations'] }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  const reservations = data?.reservations ?? [];
  const overdue = reservations.filter((item) => item.expired).length;

  return (
    <Card
      title={`Open reservations — ${reservations.length}`}
      actions={can('ADMIN', 'OPS') && (
        <button type="button" className="btn-secondary" disabled={expireNow.isPending}
                onClick={() => expireNow.mutate()}>
          {expireNow.isPending ? 'Running…' : 'Run expiry pass'}
        </button>
      )}
    >
      {overdue > 0 && (
        <p className="m-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {overdue} reservation{overdue === 1 ? ' has' : 's have'} passed their expiry and are
          waiting on the next expiry pass to be cancelled upstream.
        </p>
      )}

      <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="border-b border-ink-200">
          <tr>
            <th className="th">Reference</th><th className="th">Target</th>
            <th className="th text-right">Amount</th><th className="th">Expires</th>
            <th className="th text-right">Remaining</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {reservations.map((item) => (
            <tr key={item.id} className={item.expired ? 'bg-amber-50' : 'hover:bg-ink-50'}>
              <td className="td">
                <Link to={`/orders/${item.orderId}`} className="hover:underline">
                  <Reference value={item.agentReference} />
                </Link>
              </td>
              <td className="td mono">{item.target ?? '—'}</td>
              <td className="td text-right">
                <Money amount={item.amount ? money(item.amount) : null} currency={item.currency} />
              </td>
              <td className="td"><Timestamp value={item.expiresAt} /></td>
              <td className={`td tabular text-right ${
                item.expired ? 'font-semibold text-amber-800' : ''
              }`}>
                {duration(item.secondsRemaining)}
              </td>
            </tr>
          ))}
          {reservations.length === 0 && (
            <tr><td className="td py-10 text-center text-ink-500" colSpan={5}>
              No open reservations.
            </td></tr>
          )}
        </tbody>
      </table>
      </div>
      <p className="border-t border-ink-100 px-4 py-2 text-xs text-ink-500">
        An expired reservation means a merchant's payment flow stalled. The expiry job cancels it
        upstream so the float is released.
      </p>
    </Card>
  );
}
